import { track } from "@/utils/conversions";

export const ANNIVERSARY_CAMPAIGN_SLUG = "cc5-anniversary";
export const ANNIVERSARY_PAGE_PATH = "/5-years";

export const ANNIVERSARY_HEADLINE = "Five Years of Training Excellence";

export const ANNIVERSARY_META_DESCRIPTION =
  "Cosmetic College celebrates five years of recognised beauty and aesthetics training in London. VTCT, iTEC and CPD routes with practical, live-model-led teaching for more than 1,100 learners every year.";

export const ANNIVERSARY_HERO_LEDE =
  "Since opening our doors, Cosmetic College has helped thousands of learners move from first qualification to confident, client-ready practice.";

export const ANNIVERSARY_HERO_BODY =
  "We combine recognised awarding-body pathways with small-group practical training, live model experience and admissions support that helps you choose the right route - not just the fastest sale.";

export const ANNIVERSARY_TRAINING_PILLARS = [
  {
    id: "recognised-qualifications",
    title: "Recognised qualifications",
    detail:
      "VTCT, iTEC and CPD-accredited routes from access-level aesthetics through to advanced skin, laser and injectable training.",
    highlight: "VTCT · iTEC · CPD",
  },
  {
    id: "live-model-practice",
    title: "Live model practice",
    detail:
      "Practical training is built around real model sessions so you leave with hands-on confidence, not theory alone.",
    highlight: "Hands-on focus",
  },
  {
    id: "small-groups",
    title: "Small group teaching",
    detail:
      "Group sizes are capped so tutors can coach technique, consultation and client care properly throughout your programme.",
    highlight: "More tutor time",
  },
  {
    id: "admissions-guidance",
    title: "Admissions guidance",
    detail:
      "Not sure where to start? Our team helps you compare entry requirements, dates, payment options and the route that fits your background.",
    highlight: "Clear next steps",
  },
] as const;

export const ANNIVERSARY_COURSE_ROUTES = [
  {
    id: "aesthetics",
    title: "Aesthetics training routes",
    description:
      "From VTCT Level 3 access courses through to Level 7 injectables and advanced skin qualifications.",
    href: "/courses/aesthetics/",
    cta: "Explore aesthetics courses",
  },
  {
    id: "vtct",
    title: "VTCT qualification pathways",
    description:
      "Structured regulated routes for learners who want nationally recognised beauty and aesthetics awards.",
    href: "/courses/vtct/",
    cta: "View VTCT programmes",
  },
  {
    id: "cpd",
    title: "CPD and short courses",
    description:
      "Add individual treatments, refresh skills or build a practical CPD package around the services you want to offer.",
    href: "/build-your-cpd-package/",
    cta: "Build your CPD package",
  },
  {
    id: "career-change",
    title: "Career change support",
    description:
      "Moving from beauty, healthcare or a completely new field? Compare pathways designed for different starting points.",
    href: "/career-pathways/",
    cta: "Compare career pathways",
  },
] as const;

export const ANNIVERSARY_FAQS = [
  {
    question: "What makes Cosmetic College different after five years in training?",
    answer:
      "We focus on recognised qualifications, live model practice and small-group teaching rather than quick certificates. Admissions help you choose a route that matches your background and goals before you commit.",
  },
  {
    question: "Which awarding bodies do you work with?",
    answer:
      "We deliver VTCT and iTEC regulated qualifications alongside CPD-accredited short courses. The right awarding route depends on the treatment area and career outcome you are working toward.",
  },
  {
    question: "Do I need prior experience to start?",
    answer:
      "Not always. Some routes are designed for beginners, while others suit experienced beauty therapists or medics adding regulated qualifications. Use the course finder or speak with admissions to confirm entry requirements for your chosen programme.",
  },
  {
    question: "Can I visit the college before enrolling?",
    answer:
      "Yes. Book a college tour or join an open day to see the training centre, meet the team and ask questions about course formats, dates and payment options.",
  },
  {
    question: "How do I choose between VTCT, CPD and fast-track options?",
    answer:
      "VTCT routes suit learners who want regulated progression. CPD courses help you add specific treatments quickly. Fast-track and accelerator programmes are intensive options for learners ready to commit to a fuller schedule. Admissions can help you compare them honestly.",
  },
] as const;
export const ANNIVERSARY_QUIZ_VOUCHER_CODE = "";
export const ANNIVERSARY_EASTER_BONUS_COPY =
  "Whether you are starting from scratch, moving from beauty into aesthetics or adding an advanced treatment, we can help you choose a route that fits your background.";
export const ANNIVERSARY_OFFERS_END_COPY =
  "Compare entry requirements, course dates, payment options and practical training formats before you book.";

export const ANNIVERSARY_PROOF_POINTS = [
  {
    label: "Years supporting learners",
    value: "5",
    detail: "Five years helping students choose recognised beauty and aesthetics training.",
  },
  {
    label: "Students trained last year",
    value: "1,100+",
    detail: "More than 1,100 students trained in the last year alone.",
  },
  {
    label: "Trust stack",
    value: "VTCT, iTEC, CPD",
    detail: "Recognised training routes with practical, live-model-led teaching.",
  },
];

export const ANNIVERSARY_TESTIMONIALS = [
  {
    quote:
      "I would highly recommend Cosmetic College. I have attended two of their courses now and will continue to do so.",
    author: "Stacy Fuller",
  },
  {
    quote:
      "I have just accomplished my Foundation Anti Wrinkle course and I am absolutely thrilled with the quality of this course.",
    author: "Beata Schoeman",
  },
];

export const ANNIVERSARY_FAST_ACTION_COPY =
  ANNIVERSARY_EASTER_BONUS_COPY;

export function buildAnniversaryCallbackPath(source = ANNIVERSARY_CAMPAIGN_SLUG) {
  return `${"/request-a-callback/"}?source=${encodeURIComponent(source)}`;
}

export function buildAnniversaryQuizPath(
  voucherCode = ANNIVERSARY_QUIZ_VOUCHER_CODE,
  campaign = ANNIVERSARY_CAMPAIGN_SLUG
) {
  const params = new URLSearchParams();
  params.set("campaign", campaign);

  if (voucherCode) {
    params.set("voucher", voucherCode);
  }

  return `${"/best-aesthetic-course-for-you/"}?${params.toString()}`;
}

export function isAnniversaryCampaign(value?: string | string[] | null) {
  if (!value) {
    return false;
  }

  const testValue = Array.isArray(value) ? value[0] : value;
  return testValue === ANNIVERSARY_CAMPAIGN_SLUG;
}

export function isAnniversaryCode(code?: string | null) {
  if (!code) {
    return false;
  }

  return code.replace(/\s/g, "").toUpperCase().startsWith("CC5");
}

export function trackAnniversaryEvent(
  action: string,
  params: Record<string, any> = {}
) {
  if (typeof window === "undefined") {
    return;
  }

  track(action, {
    event_category: "Anniversary Campaign",
    event_label: ANNIVERSARY_CAMPAIGN_SLUG,
    ...params,
  });

}
